/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getCoordinatesRelativeToPage } from "utils/EventHandlingUtils";
import docApi from "api";
import { performPdfActions, getFirstURLActionInJSON, openUrl } from "pdfActionManager";
import PageCanvasCss from "components/document/page/PageCanvas.css";
import { trackHyperlinkClick } from "analytics/events";
import MouseUpEvent from "jsEvents/mouseEvents/MouseUpEvent";
import { copyTextToClipboard } from "utils/CopyTextUtils";
import { PDF_ACTIONS } from "constants/enums";
import { noop } from "utils/lodashUtils";
import { isTouchSelectionInProgress } from "dc-preview-utils/src/selectors/touchSelectors";
import { triggerHyperlinkClickEvent } from "publicApis/Events/events";
import { transformViewToPDF } from "dc-preview-utils/src/Utils/PageUtils";
import { EVENT_NAMES } from "constants/enums";

class HyperLinkManager {
  constructor() {
    this.onContextMenu = this.onContextMenu.bind(this);
  }

  handleHover() {
    document.querySelectorAll(`.${PageCanvasCss.parent}`).forEach(elem => {
      elem.style.cursor = "pointer";
    });
  }

  _hasActionType(mouseUpActions, actionType) {
    return mouseUpActions.find(action => action.actionType === actionType);
  }

  handleClick(evt, pageIndex, stores) {
    if (!isTouchSelectionInProgress(stores)) {
      const { x, y } = getCoordinatesRelativeToPage(evt);
      let extraParams = this.getCoordinatesRelativeToPDF(x, y, pageIndex, stores);
      return docApi
        .isHyperlinkClicked(x, y, pageIndex)
        .then(resp => {
          const { data } = resp;
          if (data && data.actions && data.actions[EVENT_NAMES.MOUSE_UP]) {
            const mouseUpActions = data.actions[EVENT_NAMES.MOUSE_UP];
            if (this._hasActionType(mouseUpActions, PDF_ACTIONS.JAVASCRIPT)) {
              extraParams = { ...extraParams, eventObj: new MouseUpEvent() };
            }
            performPdfActions(stores, mouseUpActions, { ...extraParams, triggeredFrom: "hyperlink" });
            trackHyperlinkClick();
            if (mouseUpActions[0] && !this._hasActionType(mouseUpActions, "URI")) {
              triggerHyperlinkClickEvent(extraParams);
            }
          }
          return true;
        })
        .catch(noop);
    }
    return false;
  }

  getContextMenu(url, extraParams, stores) {
    return [
      {
        title: "contextMenu.openLinkNewWindow",
        eventName: "openlinkinnewwindow",
        eventHandler: openUrl,
        args: [url, extraParams],
        position: 20
      },
      {
        title: "contextMenu.copyLink",
        eventName: "copylink",
        eventHandler: copyTextToClipboard,
        args: [url, stores],
        position: 20
      },
      {
        title: "separator",
        position: 20
      }
    ];
  }

  getCoordinatesRelativeToPDF(x, y, pageIndex, stores) {
    let pdfCoordinates = transformViewToPDF(x, y, pageIndex, stores);
    return {
      xCoordinate: pdfCoordinates.x,
      yCoordinate: pdfCoordinates.y,
      pageNumber: pageIndex + 1
    };
  }

  onContextMenu({ pageIndex, x, y }, stores) {
    return new Promise((resolve, reject) => {
      const extraParams = this.getCoordinatesRelativeToPDF(x, y, pageIndex, stores);
      docApi
        .isHyperlinkClicked(x, y, pageIndex)
        .then(resp => {
          let url;
          const { data } = resp;
          if (data && data.actions && data.actions[EVENT_NAMES.MOUSE_UP]) {
            url = getFirstURLActionInJSON(data.actions[EVENT_NAMES.MOUSE_UP]);
          }
          if (url) {
            resolve(this.getContextMenu(url, extraParams, stores));
          } else {
            resolve([]);
          }
        })
        .catch(() => {
          resolve([]);
        });
    });
  }
}

export { HyperLinkManager };
